import React from "react";
import { ApolloCache, FetchResult } from "@apollo/client";
import {
	MessageGroupCreateInput,
	MessageGroupCreateOrUpdateMutation,
	MessageGroupDetailDocument,
	MessageGroupDetailQuery,
	MessageGroupDetailQueryVariables,
	useMessageGroupCreateOrUpdateMutation,
} from "../../../Utilities";
import * as MessageGroupFormConversion from "./conversion";
import * as MessageGroupFormValues from "./form-values";
import { addToMessageGroupsOfUserCache, addToMessageGroupsOfUserGroupCache } from "./mutation";

/**
 * Returns a `createOrUpdate` function for the MessageGroup model. The `createOrUpdate` function translates the given
 * `formValues` to the GraphQL create input for the MessageGroup model.
 */
export function useCreateOrUpdate<
	K extends keyof MessageGroupFormValues.Create = keyof MessageGroupFormValues.Create,
>() {
	// Custom
	const [createMessageGroup] = useMessageGroupCreateOrUpdateMutation();

	return React.useCallback(
		async (formValues: MessageGroupFormValues.CreateSubset<K>) => {
			const input = MessageGroupFormConversion.toGQLCreateInput(formValues);
			const updateCache = getUpdateCacheForCreateOrUpdate(input);

			const { data, errors } = await createMessageGroup({ variables: { input }, update: updateCache });

			return { data: data?.createOrUpdateMessageGroup ?? null, errors: errors ?? null };
		},
		[createMessageGroup],
	);
}

function getUpdateCacheForCreateOrUpdate(input: MessageGroupCreateInput) {
	// Custom
	return (
		cache: ApolloCache<any>,
		result: Omit<FetchResult<MessageGroupCreateOrUpdateMutation, Record<string, any>, Record<string, any>>, "context">,
	) => {
		const createdObject = result.data?.createOrUpdateMessageGroup;
		if (!createdObject) {
			return;
		}

		cache.writeQuery<MessageGroupDetailQuery, MessageGroupDetailQueryVariables>({
			query: MessageGroupDetailDocument,
			data: { messageGroup: createdObject },
			variables: { id: createdObject.id },
		});

		if (input.userGroupIDs) {
			for (let i = 0; i < input.userGroupIDs.length; i++) {
				addToMessageGroupsOfUserGroupCache(cache, input.userGroupIDs[i], createdObject);
			}
		}

		if (input.userIDs) {
			for (let i = 0; i < input.userIDs.length; i++) {
				addToMessageGroupsOfUserCache(cache, input.userIDs[i], createdObject);
			}
		}

		cache.evict({ id: "ROOT_QUERY", fieldName: "messageGroupConnection" });

		if (input.messages && input.messages.length > 0) {
			cache.evict({ id: "ROOT_QUERY", fieldName: "messageConnection" });
		}
	};
}
